import { useEffect, useRef, useState } from 'react'
import type { DocumentData, Query, QueryDocumentSnapshot } from 'firebase/firestore'
import { onSnapshot, getCachedSnapshot } from '../lib/firestore'
import { classifyError, type ClassifiedError } from '../lib/errors'
import { chunk, FIRESTORE_IN_LIMIT } from '../lib/chunk'
import { log } from '../lib/logger'
import { isPermissionDenied, listenInBatches } from '../lib/listenInBatches'

const EMPTY_ROWS: never[] = []

function readCachedBatches<T>(
  idsKey: string,
  batchSize: number,
  buildBatchQuery: (batch: string[]) => Query<DocumentData>,
  mapDoc: (doc: QueryDocumentSnapshot<DocumentData>) => T,
): { rows: T[]; loading: boolean } {
  if (!idsKey) return { rows: EMPTY_ROWS, loading: false }
  const batches = chunk(idsKey.split(','), batchSize)
  const allRows: T[] = []
  for (const batch of batches) {
    const snap = getCachedSnapshot(buildBatchQuery(batch))
    if (!snap) return { rows: EMPTY_ROWS, loading: true }
    allRows.push(...snap.docs.map(mapDoc))
  }
  return { rows: allRows, loading: false }
}

// Firestore `in` queries cap at 30 values, so subscriptions over a list of IDs
// must be chunked. This hook owns the chunk → subscribe-per-batch → merge →
// cleanup loop and returns the flattened rows; callers reduce `rows` into
// whatever shape they need. `queryKey` folds extra query inputs (e.g. eventId)
// into the resubscribe key so the listeners rebuild when they change.
export function useBatchedSubscriptions<T>(
  ids: string[],
  buildBatchQuery: (batch: string[]) => Query<DocumentData>,
  mapDoc: (doc: QueryDocumentSnapshot<DocumentData>) => T,
  options: {
    batchSize?: number
    queryKey?: string
    /** A denied id is a normal outcome (e.g. a draft event), so it drops silently instead of being reported. */
    deniedIdsExpected?: boolean
  } = {},
): { rows: T[]; loading: boolean; pending: boolean; error: ClassifiedError | null } {
  const { batchSize = FIRESTORE_IN_LIMIT, queryKey = '', deniedIdsExpected = false } = options

  // Sorted so the same ids arriving in a different order (group members
  // before or after friends) reuse the same batches. Each new batch is a new
  // query, billed in full.
  const idsKey = [...new Set(ids)].sort().join(',')
  const hasIds = idsKey.length > 0
  const subKey = `${idsKey}|${queryKey}`

  const [storedRows, setStoredRows] = useState<T[]>(
    () => readCachedBatches(idsKey, batchSize, buildBatchQuery, mapDoc).rows,
  )
  const [storedLoading, setStoredLoading] = useState(
    () => readCachedBatches(idsKey, batchSize, buildBatchQuery, mapDoc).loading,
  )
  const [error, setError] = useState<ClassifiedError | null>(null)

  let rows = hasIds ? storedRows : EMPTY_ROWS
  let loading = hasIds ? storedLoading : false
  let currentError = error

  // Latest-ref so the effect reads current closures without resubscribing when
  // the caller passes fresh inline `buildBatchQuery` / `mapDoc` each render.
  const buildRef = useRef(buildBatchQuery)
  const mapRef = useRef(mapDoc)
  useEffect(() => {
    buildRef.current = buildBatchQuery
    mapRef.current = mapDoc
  })

  // Reset during render whenever the subscription target changes, so stale data
  // from a prior ids/queryKey combo doesn't leak through before the effect runs.
  // This pass also returns the new key's values: a caller freezing a snapshot
  // during render (useFrozenValue) would otherwise capture the old key's rows.
  const [trackedKey, setTrackedKey] = useState(subKey)
  if (trackedKey !== subKey) {
    setTrackedKey(subKey)
    const cached = readCachedBatches(idsKey, batchSize, buildBatchQuery, mapDoc)
    setStoredRows(cached.rows)
    setStoredLoading(cached.loading)
    setError(null)
    rows = cached.rows
    loading = cached.loading
    currentError = null
  }

  useEffect(() => {
    if (!hasIds) return

    return listenInBatches<T>(
      chunk(idsKey.split(','), batchSize),
      (batch, next, fail) =>
        onSnapshot(buildRef.current(batch), snap => next(snap.docs.map(mapRef.current)), fail),
      {
        onRows: setStoredRows,
        onSettled: () => setStoredLoading(false),
        onError: (err, { ids, retryId, willRetry }) => {
          // The per-id retry reports the actual culprit, if any.
          if (willRetry) return log('batched-subs', 'batch denied — retrying per id')
          if (deniedIdsExpected && ids.length === 1 && isPermissionDenied(err)) {
            return log('batched-subs', 'denied id skipped', { id: ids[0] })
          }
          // Logged before classifyError so the id lands in the reported event's breadcrumbs.
          if (retryId) log('batched-subs', 'id failed after batch retry', { id: retryId })
          const classified = classifyError(err)
          if (!retryId) setError(classified)
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stable subKey, not the input array identity
  }, [subKey, batchSize])

  // loading means "nothing to display yet" — if cached rows are available,
  // show them immediately instead of skeletons (see useFirestore.ts comment).
  // pending is the raw signal: some batch hasn't delivered, so an id missing
  // from `rows` may still arrive. Once it's false, a missing id is truly absent.
  return { rows, loading: loading && rows.length === 0, pending: loading, error: currentError }
}
